<template>
  <div class="h-full">
    <!-- Loading State -->
    <div v-if="state.isLoading" class="text-center py-8">
      <div class="text-lg text-gray-600">Loading flow...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="state.lastError" class="text-center py-8">
      <div class="text-lg text-red-600">{{ state.lastError }}</div>
      <button
        @click="loadFlow"
        class="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        Neu laden
      </button>
    </div>

    <!-- Flow Content -->
    <div v-else class="h-full">
      <component
        v-if="currentComponent"
        :is="currentComponent"
        :current-step-id="currentStep?.id"
        :total-steps="flowSteps.length"
        :current-step-index="currentStepIndex"
        @request-next="onRequestNext"
        @request-back="previousStep"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type Component,
  computed,
  onMounted,
  provide,
  ref,
  shallowRef,
} from 'vue'
import stepResolver from '@/modules/wizard/services/step-resolver.service'
import wizardFlowService from '@/modules/wizard/services/wizard-flow.service'
import { WizardPreconditions } from '@/modules/wizard/types/WizardPreconditions'
import { WizardFlowStepEvents } from '@/modules/wizard/types/WizardFlowStep'
import accountStore from '@/stores/account.store'
import { UniversityEntranceQualificationType } from '@/types/enums'

const props = defineProps<{
  flowName: string
  context?: WizardPreconditions
}>()

const emit = defineEmits<WizardFlowStepEvents>()

const state = ref({
  isLoading: true,
  lastError: null as string | null,
})

const componentRegistry = shallowRef<Record<string, Component>>({})

const currentStepIndex = ref(0)
const flowSteps = ref<Array<{ id: string }>>([])
const currentStep = computed(() => flowSteps.value[currentStepIndex.value])

// Enhanced context with fallback values
const enhancedContext = computed(() => {
  if (!props.context) return undefined

  return {
    ...props.context,
    // Fallback for hasAbroadDiploma based on account data
    hasAbroadDiploma:
      props.context.hasUniversityEntranceQualificationFromAbroad ??
      accountStore.state.account?.universityEntranceQualification ===
        UniversityEntranceQualificationType.UNIVERSITY_ENTRANCE_DIPLOMA_FROM_ABROAD,
  }
})

// Check if a step should be rendered based on conditions
const shouldRenderStep = (
  step: { id: string; if?: string[]; unless?: string[] },
  context: WizardPreconditions | undefined,
): boolean => {
  if (!context) return true

  let ifResult = true
  let unlessResult = true

  if (step.if) {
    ifResult = step.if.every((condition) => {
      const value = context[condition as keyof WizardPreconditions]
      return typeof value === 'boolean' && value === true
    })
  }

  if (step.unless) {
    unlessResult = step.unless.some((condition) => {
      const value = context[condition as keyof WizardPreconditions]
      return typeof value === 'boolean' && value === false
    })
  }

  return ifResult && unlessResult
}

// Get the next available step based on conditions
const getNextAvailableStep = (
  currentIndex: number,
  context: WizardPreconditions | undefined,
): number => {
  for (let i = currentIndex + 1; i < flowSteps.value.length; i++) {
    if (shouldRenderStep(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No more available steps
}

// Get the previous available step based on conditions
const getPreviousAvailableStep = (
  currentIndex: number,
  context: WizardPreconditions | undefined,
): number => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (shouldRenderStep(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No previous available steps
}

const currentComponent = computed(() => {
  if (!currentStep.value) return null

  // Check if current step should be rendered
  if (!shouldRenderStep(currentStep.value, enhancedContext.value)) {
    return null
  }

  try {
    const componentLoader = stepResolver.resolve(currentStep.value.id)
    const componentPath = componentLoader.toString()
    const component = componentRegistry.value[componentPath] || null
    return component
  } catch (error) {
    console.error(`Error resolving step ${currentStep.value.id}:`, error)
    return null
  }
})

// Called when a step requests auto-advance (after its internal logic)
const onRequestNext = () => nextStep()

const loadComponent = async (
  componentLoader: () => Promise<{ default: Component }>,
) => {
  const componentPath = componentLoader.toString()
  if (componentRegistry.value[componentPath]) {
    return componentRegistry.value[componentPath]
  }

  try {
    const component = await componentLoader()
    componentRegistry.value[componentPath] = component.default
    return component.default
  } catch (err) {
    console.error(`Failed to load component: ${componentPath}`, err)
    return null
  }
}

const loadFlow = async () => {
  try {
    state.value.isLoading = true
    state.value.lastError = null

    const flowModule = await import(
      `@/modules/wizard/flows/${props.flowName}.json`
    )
    const flowData = flowModule.default

    if (!flowData.steps || !Array.isArray(flowData.steps)) {
      throw new Error('Invalid flow format: missing or invalid steps array')
    }

    flowSteps.value = flowData.steps

    // Load saved progress and resume from last position
    const savedProgress = wizardFlowService.getProgress(props.flowName)
    if (
      savedProgress !== null &&
      savedProgress >= 0 &&
      savedProgress < flowData.steps.length
    ) {
      currentStepIndex.value = savedProgress
    } else {
      currentStepIndex.value = 0
    }

    // Save initial step progress only if we're starting from the beginning
    if (
      currentStepIndex.value === 0 &&
      flowData.steps.length > 0 &&
      props.flowName
    ) {
      wizardFlowService.saveProgress(props.flowName, 0)
    }

    // Preload all components for the flow
    for (const step of flowData.steps) {
      try {
        const componentLoader = stepResolver.resolve(step.id)
        await loadComponent(componentLoader)
      } catch (err) {
        console.error(`Failed to preload component for step ${step.id}:`, err)
      }
    }
  } catch (err) {
    console.error('Error loading flow:', err)
    state.value.lastError = `Failed to load flow: ${props.flowName}`
  } finally {
    state.value.isLoading = false
  }
}

const nextStep = () => {
  const nextIndex = getNextAvailableStep(
    currentStepIndex.value,
    enhancedContext.value,
  )
  if (nextIndex !== -1) {
    currentStepIndex.value = nextIndex
    wizardFlowService.saveProgress(props.flowName, nextIndex)
  } else {
    // No more steps available, flow is complete
    emit('flow-complete')
  }
}

const previousStep = () => {
  const prevIndex = getPreviousAvailableStep(
    currentStepIndex.value,
    enhancedContext.value,
  )
  if (prevIndex !== -1) {
    currentStepIndex.value = prevIndex
    wizardFlowService.saveProgress(props.flowName, prevIndex)
  }
}

provide('wizardContext', {
  currentStepId: computed(() => currentStep.value?.id),
  totalSteps: computed(() => flowSteps.value.length),
  currentStepIndex: computed(() => currentStepIndex.value),
})

onMounted(() => loadFlow())
</script>
